import * as React from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Select, MenuItem, FormControl, InputLabel,
    Typography
} from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { useApi } from 'context/ApiProvider';
import { Patient } from 'models/Patient';
import { useViewerStore } from 'context/ViewerContext';
import { MeasurementSystem } from 'components/Viewer3d/consts';
import { convertKgToViewerUnit, convertMmToViewerUnit, convertUnitToDefault, convertWeightUnitToDefault, LengthUnit, WeightUnit } from 'Utils/MeasurementUtils';

type Gender = 'Male' | 'Female';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

type CreatePatientValues = {
    name: string;
    gender?: Gender;
    age?: number | null;
    weight?: number | null;
    height?: number | null;
};

// type BackendPatient = {
//     patientId: number;
//     name: string;
//     age?: number | null;
//     weight?: number | null;
//     height?: number | null;
//     gender?: 'Male' | 'Female' | null;
//     createdAt?: string | null;
// };

type CreatePatientDialogProps = {
    open: boolean;
    onClose: () => void;
    onSave: (updated?: Patient) => void; // <— change this
    initial?: Partial<CreatePatientValues>;
    zIndex?: number;
};

export default function CreatePatientDialog({
    open,
    onClose,
    onSave,
    initial,
    zIndex = 3000,
}: CreatePatientDialogProps) {
    const [name, setName] = React.useState(initial?.name ?? '');
    const [gender, setGender] = React.useState<Gender>(initial?.gender ?? 'Male');
    const [age, setAge] = React.useState<string>(initial?.age != null ? String(initial.age) : '');
    const [weight, setWeight] = React.useState<string>(initial?.weight != null ? String(initial.weight) : '');
    const [height, setHeight] = React.useState<string>(initial?.height != null ? String(initial.height) : '');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const { isDarkMode } = useAppTheme();
    const api = useApi();
    const designId = useSelectedDesignStore((state) => state.designId);

    const measurementSystem: MeasurementSystem = useViewerStore(state => state.measurementSystem);
    const defaultLengthUnits: Record<MeasurementSystem, LengthUnit> = {
        [MeasurementSystem.metric]: 'cm',
        [MeasurementSystem.imperial]: 'inch',
    };
    const getDefaultLengthUnit = () => {
        return defaultLengthUnits[measurementSystem];
    }
    const defaultWeightUnits: Record<MeasurementSystem, WeightUnit> = {
        [MeasurementSystem.metric]: 'kg',
        [MeasurementSystem.imperial]: 'lb',
    };
    const getDefaultWeightUnit = () => {
        return defaultWeightUnits[measurementSystem];
    }

    React.useEffect(() => {
        if (!open) return;
        setName(initial?.name ?? '');
        setGender(initial?.gender ?? 'Male');
        setAge(initial?.age !== null && initial?.age !== undefined ? String(initial.age) : '');
        setWeight(initial?.weight !== null && initial?.weight !== undefined ? String(convertKgToViewerUnit(initial.weight)) : '');
        if (initial?.height !== null && initial?.height !== undefined) {
            const localPatientHeight = measurementSystem === MeasurementSystem.metric ? convertMmToViewerUnit(initial.height) / 10 :convertMmToViewerUnit(initial.height);
            setHeight(String(localPatientHeight));
        } else {
            setHeight('');
        }
        setError(null);
    }, [open, initial]);

    const handleSave = async () => {
        setError(null);

        const values: CreatePatientValues = {
            name: name.trim(),
            gender,
            age: age === '' ? null : Number(age),
            weight: weight === '' ? null : convertWeightUnitToDefault(Number(weight), getDefaultWeightUnit()),
            height: height === '' ? null : convertUnitToDefault(Number(height), getDefaultLengthUnit()),
        };

        if (!values.name) { setError('Name is required.'); return; }
        if (!designId) { setError('No design selected.'); return; }

        const data: Record<string, unknown> = {
            name: values.name,
            gender: values.gender?.toLowerCase(), // keep if your API expects lowercase
        };
        if (values.age !== null) data.age = values.age;
        if (values.weight !== null) data.weight = values.weight;
        if (values.height !== null) data.height = values.height;

        const patient = new Patient();
        patient.age = values.age ?? undefined;
        patient.gender = values.gender;
        patient.weight = values.weight ?? undefined;
        patient.height = values.height ?? undefined;
        patient.name = values.name;

        setSaving(false);
        onSave(patient);
        onClose();

        // try {
        //     // IMPORTANT: capture the server’s response
        //     const res = await api.put(`/api/patients/${designId}`, data);

        //     // Your API wrapper may return { ok, body }. Safely pick the patient:
        //     const savedPatient: BackendPatient | undefined =
        //         res?.body?.patient ?? res?.body ?? undefined;

        //     // Hand the fresh patient up to the parent
        //     onSave(savedPatient);

        //     onClose();
        // } catch (e: any) {
        //     setError(e?.response?.data?.message || e?.message || 'Failed to save patient.');
        // } finally {
        //     setSaving(false);
        // }
    };


    const surfaceBg = isDarkMode ? '#333' : '#fff';
    const textColor = isDarkMode ? '#fff' : '#000';
    const borderColor = '#ccc';
    const headerAccent = isDarkMode ? '#fff' : '#076596';

    const inputSx = {
        '& .MuiInputBase-root': {
            color: textColor,
            backgroundColor: surfaceBg,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor,
        },
        '& .MuiInputLabel-root': {
            color: textColor,
        },
        '& .MuiSvgIcon-root': {
            color: textColor,
        },
    } as const;

    const menuPaperProps = {
        MenuProps: {
            sx: {
                bgcolor: surfaceBg,
                color: textColor,
                '& .MuiMenuItem-root': {
                    color: textColor,
                },
            },
        },
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
            fullWidth
            maxWidth="sm"
            keepMounted
            disablePortal={false}
            PaperProps={{
                sx: {
                    zIndex,
                    position: 'relative',
                    bgcolor: surfaceBg,
                    color: textColor,
                    borderRadius: 2,
                    boxShadow: 24,
                },
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    zIndex: zIndex - 1,
                    pointerEvents: 'none',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: `1px solid ${borderColor}`,
                    bgcolor: surfaceBg,
                }}
            >
                <Typography variant="inherit" sx={{ color: headerAccent, textAlign: 'left' }}>
                    Create Patient
                </Typography>
            </DialogTitle>

            <DialogContent
                sx={{
                    pt: 2,
                    display: 'grid',
                    gap: 2,
                    paddingTop: '10px',
                    bgcolor: surfaceBg,
                    color: textColor,
                }}
            >
                <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    sx={{ mt: 2, ...inputSx }}
                    InputLabelProps={{ sx: { color: textColor } }}
                />

                <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="gender-label" sx={{ color: textColor }}>
                        Gender
                    </InputLabel>
                    <Select
                        labelId="gender-label"
                        label="Gender"
                        value={gender}
                        onChange={(e) => setGender(e.target.value as Gender)}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: surfaceBg,
                                    color: textColor,
                                    '& .MuiMenuItem-root': {
                                        color: textColor,
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label="Age"
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    fullWidth
                    sx={inputSx}
                    InputLabelProps={{ sx: { color: textColor } }}
                />

                <TextField
                    label={`Weight (${getDefaultWeightUnit()})`}
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    fullWidth
                    sx={inputSx}
                    InputLabelProps={{ sx: { color: textColor } }}
                />

                <TextField
                    label={`Height (${getDefaultLengthUnit()})`}
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    fullWidth
                    sx={inputSx}
                    InputLabelProps={{ sx: { color: textColor } }}
                />

                {error && (
                    <div
                        style={{
                            color: '#ff6b6b',
                            fontSize: '0.9rem',
                            marginTop: '4px',
                        }}
                    >
                        {error}
                    </div>
                )}
            </DialogContent>

            <DialogActions sx={{ mb: 2, mr: 2, borderTop: `1px solid ${borderColor}` }}>
                <Button
                    onClick={onClose}
                    disabled={saving}
                    variant="outlined"
                    sx={{
                        mt: 1,
                        color: textColor,
                        borderColor: '#aaa',
                        width: '80px',
                        fontWeight: 500,
                        '&:hover': { borderColor: '#aaa' },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!name.trim() || saving}
                    autoFocus
                    sx={{
                        mt: 1,
                        color: '#fff',
                        borderColor: '#aaa',
                        backgroundColor: '#076596',
                        width: '80px',
                        fontWeight: 500,
                        px: 4,
                        '&:hover': { backgroundColor: '#0a6fb4' },
                    }}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
